import { FC, useEffect, useMemo, useRef, useState } from "react";
// 方法
import { throttle, isNumber } from 'lodash';
import ScrollContext from '../context/scrollContext';
import useVirtualTableContext from '../hooks/useVirtualTableContext';

interface IWrapperInfo {
  /** 列表行高 */
  rowHeight: number;
  /** 滚动容器可见区域高度 */
  tableScrollHeight: number;
};

const VirtualTableTable: FC<any> = props => {
  const { style, children, ...rest } = props;
  // 数据的总条数
  const [totalLen, setTotalLen] = useState<number>(
    children[2]?.props?.data?.length ?? 0,
  );

  /* 滚动容器信息，可见区域高度和行高 */
  const [wrapperInfo, setWrapperInfo] = useState<IWrapperInfo>({tableScrollHeight: 350, rowHeight: 35});
  /** 滚动条的高度 */
  const [paddingTop, setPaddingTop] = useState<number>(0);
  const {start, offsetStart, dispatchVirtualTable} = useVirtualTableContext();
  const wrapTableRef = useRef<HTMLDivElement>(null);
  const timer = useRef<any>();
  /** 获取列表总条数 1 */
  useEffect(() => {
    /** 总条数 */
    const newTotalLen = children?.[2]?.props?.data?.length;
    if (isNumber(newTotalLen) && newTotalLen !== totalLen) {
      setTotalLen(newTotalLen);
    }
  }, [children]);
  /** 渲染的条数 */
  const renderLen = useMemo<number>(() => {
    const { tableScrollHeight, rowHeight } = wrapperInfo;
    let temp = 1;
    if (rowHeight && totalLen && tableScrollHeight) {
      if (tableScrollHeight <= 0) {
        temp = 0;
      } else {
        /** 滚动区域可渲染的行数+头部预加载3行+尾部预加载三行 */
        let tempRenderLen = ((tableScrollHeight / rowHeight) | 0) + 3;
        temp = tempRenderLen > totalLen ? totalLen : tempRenderLen;
      }
    }
    return temp;
  }, [wrapperInfo, totalLen]);
  /** 获取滚动区域高度和行高 */
  useEffect(() => {
    // TODO
    // if (timer.current) clearTimeout(timer.current);
    // timer.current = setTimeout(() => {
    //   const wrapRef = wrapTableRef?.current?.parentNode as HTMLElement;
    //   const rowRef = wrapTableRef.current?.getElementsByClassName('ant-table-row')?.[0];
    //   const newTableScrollHeight = wrapRef?.clientHeight || 350;
    //   const newRowHeight = rowRef?.clientHeight || 35;
    //   if (newTableScrollHeight !== wrapperInfo.tableScrollHeight || newRowHeight !== wrapperInfo.rowHeight)
    //   setWrapperInfo({tableScrollHeight: newTableScrollHeight, rowHeight: newRowHeight});
    // }, 200);
    // return () => {
    //   clearTimeout(timer.current);
    // };
    const wrapRef = wrapTableRef?.current?.parentNode as HTMLElement;
    if (!wrapRef) return;
    let resizeObserver: any = new ResizeObserver((entries) => {
      timer.current && clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const entrieTarget = entries[0].target;
        const rowRef = entrieTarget?.getElementsByClassName('ant-table-row')?.[0];
        const newTableScrollHeight = entrieTarget.clientHeight || 350;
        const newRowHeight = rowRef?.clientHeight || 35;
        setWrapperInfo({tableScrollHeight: newTableScrollHeight, rowHeight: newRowHeight});
      }, 300);
    });
    resizeObserver.observe(wrapRef);
    return () => {
      resizeObserver.disconnect();
      resizeObserver = null;
    };
  }, [wrapTableRef]);
  /** 监听滚动事件 */
  useEffect(() => {
    const wrapRef = wrapTableRef?.current?.parentNode as HTMLElement;
    const throttleScroll = throttle(() => {
      const scrollTop: number = wrapRef?.scrollTop ?? 0;
      const { rowHeight } = wrapperInfo;
      /** 滚动条总的高度 */
      const scrollHeight = totalLen * rowHeight;
      /** 可滚动的最大高度 */
      const maxScrollTop = scrollHeight - (renderLen * rowHeight);
      /** 头部未渲染区域的高度 */
      const newScrollTop = scrollTop >= maxScrollTop ? maxScrollTop : scrollTop;
      /** 滚动区域中的第一条 */
      const newScrollStart = rowHeight ? ((newScrollTop / rowHeight) | 0) : 0;
      /** 渲染中的第一条 */
      const newStart = newScrollStart > 0 ? newScrollStart : 0;
      /** 渲染中的第一条向上偏移量 */
      const newOffsetStart = scrollTop >= maxScrollTop ? 0 : (newScrollTop % rowHeight);
      dispatchVirtualTable('change', {offsetStart: newOffsetStart, start: newStart });
      setPaddingTop(newScrollTop);
    }, 120);
    wrapRef && wrapRef.addEventListener('scroll', throttleScroll);
    return () => {
      wrapRef && wrapRef.removeEventListener('scroll', throttleScroll);
    };
  }, [wrapTableRef, totalLen, renderLen, wrapperInfo]);
  /** 滚动区域高度 */
  const tableHeight = useMemo(() => {
    let temp: string | number = 'auto';
    const { rowHeight } = wrapperInfo;
    if (rowHeight && totalLen) {
      temp = rowHeight * totalLen;
    }
    return temp;
  }, [totalLen, wrapperInfo]);
  if (!Array.isArray(children) || children.length === 0) return null;
  return (
    <div
      className="virtuallist"
      ref={wrapTableRef}
      style={{
        width: '100%',
        position: 'relative',
        boxSizing: 'border-box',
        height: tableHeight,
        paddingTop: paddingTop,
      }}
    >
      <ScrollContext.Provider
        value={{
          renderLen,
          start,
          offsetStart,
        }}
      >
        <table
          {...rest}
          style={{
            ...style,
            position: 'relative',
          }}
        >
          {children}
        </table>
      </ScrollContext.Provider>
    </div>
  );
};

export default VirtualTableTable;
